import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import HistoryIcon from "@mui/icons-material/History";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import GroupIcon from "@mui/icons-material/Group";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CategoryIcon from "@mui/icons-material/Category";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import WarningIcon from "@mui/icons-material/Warning";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { logout } from "../../services/user";
import { useDirty } from "../../context/dirty";

export const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmLeave, setDirty } = useDirty();

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    if (!confirmLeave()) return;
    setDirty(false);
    logout();
    navigate("/?license=FZPZ55");
  };

  const menuItems = [
    { path: "/dashboard", icon: DashboardIcon, label: "Dashboard" },
    { path: "/dashboard/newfile", icon: DirectionsCarIcon, label: "Nuevo Registro" },
    { path: "/dashboard/history", icon: HistoryIcon, label: "Historial" },
    { path: "/dashboard/customers", icon: GroupIcon, label: "Clientes" },
    { path: "/dashboard/brands", icon: CategoryIcon, label: "Marcas" },
    { path: "/dashboard/services", icon: BuildCircleIcon, label: "Tipos de Servicio" },
    { path: "/dashboard/whatsapp", icon: WhatsAppIcon, label: "WhatsApp" },
    { path: "/dashboard/duplicates", icon: ContentCopyIcon, label: "Duplicados" },
    { path: "/dashboard/users", icon: PeopleIcon, label: "Usuarios" },
    { path: "/dashboard/newuser", icon: PersonAddIcon, label: "Nuevo Usuario" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-sidebar-bg rounded-lg border border-sidebar-accent text-white"
      >
        {isOpen ? <CloseIcon /> : <MenuIcon />}
      </button>

      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div
        className={`fixed lg:sticky top-0 h-screen bg-sidebar-bg border-r border-sidebar-accent transition-transform duration-300 z-40 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 w-64 overflow-y-auto`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-sidebar-accent">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <DirectionsCarIcon className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">GaragePro</h1>
                <p className="text-xs text-gray-500">Sistema de Gestión</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    if (location.pathname !== item.path && !confirmLeave()) return;
                    if (location.pathname !== item.path) setDirty(false);
                    navigate(item.path);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                      : "text-gray-400 hover:bg-sidebar-accent hover:text-white"
                  }`}
                >
                  <Icon sx={{ fontSize: 20 }} />
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.badge && (
                    <span
                      className={`ml-auto w-2.5 h-2.5 rounded-full ${
                        item.badge === "green" ? "bg-green-400" : "bg-yellow-400"
                      }`}
                    />
                  )}
                </button>
              );
            })}
          </nav>

          <div className="p-4 border-t border-sidebar-accent">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200"
            >
              <LogoutIcon sx={{ fontSize: 20 }} />
              <span className="text-sm font-medium">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </div>

      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-sidebar-bg rounded-xl border border-orange-500/30 w-full max-w-md">
            <div className="px-6 py-4 border-b border-orange-500/30 bg-orange-500/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <WarningIcon className="text-orange-400" sx={{ fontSize: 24 }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Cerrar Sesión</h3>
                  <p className="text-sm text-gray-400">¿Estás seguro?</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-300 mb-4">
                ¿Estás seguro de que deseas cerrar sesión?
              </p>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                <p className="text-sm text-orange-400 flex items-start gap-2">
                  <WarningIcon sx={{ fontSize: 16, marginTop: "2px" }} />
                  <span>
                    Deberás iniciar sesión nuevamente para acceder al sistema.
                  </span>
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-sidebar-accent flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <LogoutIcon sx={{ fontSize: 18 }} />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
